/* assets/kcc-product.js
 * ---------------------------------------------------------------------------
 * Variant matrix, gallery, quantity stepper and add-to-cart for
 * sections/main-product.liquid. Vanilla JS, no libraries, no build step.
 *
 * Everything here is progressive enhancement. With JS off the section still
 * works: a real <select name="id"> inside <noscript> picks the variant and the
 * <form action="/cart/add"> posts normally.
 *
 * CART CONTRACT — this file never calls /cart/add.js itself. It hands off to
 * window.KCCCart (assets/kcc-cart.js), which today exposes:
 *
 *     window.KCCCart.add({ id, quantity, properties, selling_plan })  -> promise
 *
 * so that is the call we make. `addItem` is accepted as an alias, and if either
 * function declares 2 or more parameters it is called positionally instead:
 *
 *     window.KCCCart.add(variantId, quantity, sellingPlanId)
 *
 * If the call returns a promise we wait on it; a rejection falls back to a
 * native form submit. If window.KCCCart is absent at submit time we do not
 * preventDefault at all, so the browser posts the form. Either way the item
 * lands in the cart.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  function parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function classesFrom(el, attr) {
    if (!el) return [];
    return (el.getAttribute(attr) || '').split(/\s+/).filter(Boolean);
  }

  function toggleClasses(el, list, on) {
    if (!el) return;
    for (var i = 0; i < list.length; i++) el.classList.toggle(list[i], on);
  }

  function init(root) {
    if (!root || root.kccProductReady) return;
    root.kccProductReady = true;

    var data = parseJSON(root.querySelector('[data-kcc-variant-data]'));
    if (!data || !data.variants || !data.variants.length) return;

    var variants = data.variants;
    var optionCount = data.option_count || 1;

    var form = root.querySelector('[data-kcc-form]');
    var idInput = root.querySelector('[data-kcc-variant-id]');
    var picker = root.querySelector('[data-kcc-variant-picker]');
    var priceEl = root.querySelector('[data-kcc-price]');
    var compareEl = root.querySelector('[data-kcc-compare-price]');
    var saveEl = root.querySelector('[data-kcc-save-badge]');
    var addBtn = root.querySelector('[data-kcc-add]');
    var addText = root.querySelector('[data-kcc-add-text]');
    var qtyInput = root.querySelector('[data-kcc-qty-input]');
    var liveRegion = root.querySelector('[data-kcc-live]');
    var optionInputs = Array.prototype.slice.call(
      root.querySelectorAll('[data-kcc-option-input]')
    );

    var unavailableClasses = classesFrom(picker, 'data-kcc-unavailable-class');
    var labels = {
      add: (addBtn && addBtn.getAttribute('data-kcc-label-add')) || 'Add to Cart',
      soldOut: (addBtn && addBtn.getAttribute('data-kcc-label-sold-out')) || 'Sold Out',
      unavailable:
        (addBtn && addBtn.getAttribute('data-kcc-label-unavailable')) || 'Unavailable',
      adding: (addBtn && addBtn.getAttribute('data-kcc-label-adding')) || 'Adding…'
    };

    /* The hidden id input is disabled in the markup so that, with JS off, the
       <noscript> select is the only thing posting `id`. Turn it on now. */
    if (idInput) idInput.disabled = false;

    var current = null;

    /* ---------------------------------------------------------------- variants */

    function selectedOptions() {
      var out = [];
      for (var i = 0; i < optionCount; i++) {
        var checked = root.querySelector(
          '[data-kcc-option-input][data-kcc-option-index="' + i + '"]:checked'
        );
        out.push(checked ? checked.value : null);
      }
      return out;
    }

    function matches(variant, opts) {
      for (var i = 0; i < opts.length; i++) {
        if (opts[i] === null) continue;
        if (variant.options[i] !== opts[i]) return false;
      }
      return true;
    }

    function findVariant(opts) {
      var partial = null;
      for (var i = 0; i < variants.length; i++) {
        if (!matches(variants[i], opts)) continue;
        if (variants[i].available) return variants[i];
        if (!partial) partial = variants[i];
      }
      return partial;
    }

    /* Grey out option values that cannot be reached from the current selection. */
    function refreshOptionAvailability(opts) {
      if (!unavailableClasses.length) return;
      optionInputs.forEach(function (input) {
        var index = parseInt(input.getAttribute('data-kcc-option-index'), 10);
        if (isNaN(index)) return;
        var probe = opts.slice();
        probe[index] = input.value;
        var reachable = false;
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].available && matches(variants[i], probe)) {
            reachable = true;
            break;
          }
        }
        var label = input.id
          ? root.querySelector('label[for="' + input.id + '"]')
          : null;
        toggleClasses(label, unavailableClasses, !reachable);
        input.setAttribute('data-kcc-reachable', reachable ? 'true' : 'false');
      });
    }

    /* ---------------------------------------------------------------- gallery */

    function showMedia(mediaId) {
      if (!mediaId) return;
      var images = root.querySelectorAll('[data-kcc-gallery-image]');
      var found = false;
      for (var i = 0; i < images.length; i++) {
        if (images[i].getAttribute('data-kcc-media-id') === String(mediaId)) found = true;
      }
      if (!found) return;
      for (var j = 0; j < images.length; j++) {
        images[j].classList.toggle(
          'hidden',
          images[j].getAttribute('data-kcc-media-id') !== String(mediaId)
        );
      }
      var thumbs = root.querySelectorAll('[data-kcc-thumb]');
      for (var k = 0; k < thumbs.length; k++) {
        var isActive = thumbs[k].getAttribute('data-kcc-media-id') === String(mediaId);
        toggleClasses(thumbs[k], classesFrom(thumbs[k], 'data-kcc-active-class'), isActive);
        toggleClasses(
          thumbs[k],
          classesFrom(thumbs[k], 'data-kcc-inactive-class'),
          !isActive
        );
        thumbs[k].setAttribute('aria-current', isActive ? 'true' : 'false');
      }
    }

    /* ---------------------------------------------------------- selling plans */

    function sellingPlanInputs() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-kcc-selling-plan]'));
    }

    function activeSellingPlanId() {
      var inputs = sellingPlanInputs();
      for (var i = 0; i < inputs.length; i++) {
        if (!inputs[i].disabled && inputs[i].checked) return inputs[i].value;
      }
      return null;
    }

    function allocationFor(variant, planId) {
      if (!variant || !planId || !variant.selling_plan_allocations) return null;
      for (var i = 0; i < variant.selling_plan_allocations.length; i++) {
        var a = variant.selling_plan_allocations[i];
        if (String(a.selling_plan_id) === String(planId)) return a;
      }
      return null;
    }

    /* ---------------------------------------------------------------- render */

    function render() {
      var opts = selectedOptions();
      var variant = findVariant(opts);
      current = variant;

      refreshOptionAvailability(opts);

      if (idInput) idInput.value = variant ? variant.id : '';

      var planId = activeSellingPlanId();
      var allocation = allocationFor(variant, planId);

      if (priceEl) {
        if (allocation) priceEl.innerHTML = allocation.price_formatted;
        else if (variant) priceEl.innerHTML = variant.price_formatted;
      }

      var comparePrice = allocation
        ? allocation.compare_at_price
        : variant
        ? variant.compare_at_price
        : 0;
      var currentPrice = allocation
        ? allocation.price
        : variant
        ? variant.price
        : 0;
      var onSale = !!(comparePrice && comparePrice > currentPrice);

      if (compareEl) {
        compareEl.classList.toggle('hidden', !onSale);
        if (onSale) {
          compareEl.innerHTML = allocation
            ? allocation.compare_at_formatted
            : variant.compare_at_formatted;
        }
      }
      if (saveEl) saveEl.classList.toggle('hidden', !onSale);

      if (addBtn) {
        var buyable = !!(variant && variant.available);
        addBtn.disabled = !buyable;
        if (addText) {
          addText.textContent = !variant
            ? labels.unavailable
            : variant.available
            ? labels.add
            : labels.soldOut;
        }
      }

      if (variant) {
        showMedia(variant.media_id);
        var inEditor = !!(window.Shopify && window.Shopify.designMode);
        if (window.history && window.history.replaceState && data.product_url && !inEditor) {
          window.history.replaceState({}, '', data.product_url + '?variant=' + variant.id);
        }
      }

      root.dispatchEvent(
        new CustomEvent('kcc:variant:change', {
          bubbles: true,
          detail: { variant: variant, sellingPlanId: planId }
        })
      );
    }

    /* ---------------------------------------------------------------- events */

    optionInputs.forEach(function (input) {
      input.addEventListener('change', render);
    });

    root.addEventListener('click', function (event) {
      var thumb = event.target.closest ? event.target.closest('[data-kcc-thumb]') : null;
      if (thumb && root.contains(thumb)) {
        event.preventDefault();
        showMedia(thumb.getAttribute('data-kcc-media-id'));
        return;
      }

      var qtyBtn = event.target.closest ? event.target.closest('[data-kcc-qty-btn]') : null;
      if (qtyBtn && qtyInput) {
        event.preventDefault();
        var delta = parseInt(qtyBtn.getAttribute('data-kcc-qty-delta'), 10) || 0;
        var next = (parseInt(qtyInput.value, 10) || 1) + delta;
        if (next < 1) next = 1;
        qtyInput.value = next;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      var typeBtn = event.target.closest
        ? event.target.closest('[data-kcc-purchase-type]')
        : null;
      if (typeBtn) {
        event.preventDefault();
        setPurchaseType(typeBtn.getAttribute('data-kcc-purchase-type'));
      }
    });

    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var value = parseInt(qtyInput.value, 10);
        if (isNaN(value) || value < 1) qtyInput.value = 1;
      });
    }

    sellingPlanInputs().forEach(function (input) {
      input.addEventListener('change', render);
    });

    /* ------------------------------------------------- one-time / subscribe */

    function setPurchaseType(type) {
      var buttons = root.querySelectorAll('[data-kcc-purchase-type]');
      for (var i = 0; i < buttons.length; i++) {
        var isActive = buttons[i].getAttribute('data-kcc-purchase-type') === type;
        toggleClasses(buttons[i], classesFrom(buttons[i], 'data-kcc-active-class'), isActive);
        toggleClasses(
          buttons[i],
          classesFrom(buttons[i], 'data-kcc-inactive-class'),
          !isActive
        );
        buttons[i].setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }

      var subscribing = type === 'subscription';
      var panel = root.querySelector('[data-kcc-selling-plan-panel]');
      if (panel) panel.classList.toggle('hidden', !subscribing);

      var inputs = sellingPlanInputs();
      var anyChecked = false;
      inputs.forEach(function (input) {
        input.disabled = !subscribing;
        if (input.checked) anyChecked = true;
      });
      if (subscribing && !anyChecked && inputs.length) inputs[0].checked = true;

      var note = root.querySelector('[data-kcc-subscription-note]');
      if (note) note.classList.toggle('hidden', !subscribing);

      render();
    }

    /* ------------------------------------------------------------ add to cart */

    function handOffToCart(variantId, quantity, sellingPlanId) {
      var cart = window.KCCCart;
      if (!cart) return undefined;
      var fn = null;
      if (typeof cart.addItem === 'function') fn = cart.addItem;
      else if (typeof cart.add === 'function') fn = cart.add;
      if (!fn) return undefined;

      if (fn.length >= 2) return fn.call(cart, variantId, quantity, sellingPlanId);

      var payload = { id: variantId, quantity: quantity };
      if (sellingPlanId) payload.selling_plan = sellingPlanId;
      return fn.call(cart, payload);
    }

    function setBusy(busy) {
      if (!addBtn) return;
      addBtn.disabled = busy || !(current && current.available);
      addBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (addText) {
        if (busy) addText.textContent = labels.adding;
        else if (current && current.available) addText.textContent = labels.add;
      }
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        if (!current || !current.available) {
          event.preventDefault();
          return;
        }
        var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var result = handOffToCart(current.id, quantity, activeSellingPlanId());

        /* No cart drawer available — let the browser post the form. */
        if (result === undefined) return;

        event.preventDefault();
        setBusy(true);

        Promise.resolve(result)
          .then(function () {
            setBusy(false);
            if (liveRegion) {
              liveRegion.textContent = quantity + ' added to cart.';
            }
          })
          .catch(function () {
            setBusy(false);
            form.submit();
          });
      });
    }

    /* Sync everything to whatever the server rendered. */
    render();
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-kcc-product]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor re-render */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
